import type { InferPrimitiveTypeName, PrimitiveType, PrimitiveTypeAnyDateTime, PrimitiveTypeBoolean, PrimitiveTypeDateTime, PrimitiveTypeDateTimeWithTimezone, PrimitiveTypeDecimal, PrimitiveTypeFloat, PrimitiveTypeInteger, PrimitiveTypeInteger64, PrimitiveTypeJson, PrimitiveTypeName, PrimitiveTypeNumber, PrimitiveTypeParams, PrimitiveTypeString, PrimitiveTypeStringEncodedNumber, PrimitiveTypeUnsupported, PrimitiveTypeUuid } from '#layers/data-model/types/schema/primitive-type';
import { isDeepEqual } from '#layers/base/public';
import { DecimalPrecisionKind, PRIMITIVE_TYPE_BOOLEAN, PRIMITIVE_TYPE_DECIMAL, PRIMITIVE_TYPE_JSON, PRIMITIVE_TYPE_STRING, PRIMITIVE_TYPE_UNSUPPORTED, PRIMITIVE_TYPE_UUID, PRIMITIVE_TYPES_DATETIME, PRIMITIVE_TYPES_FLOAT, PRIMITIVE_TYPES_INTEGER, PRIMITIVE_TYPES_INTEGER_64, PRIMITIVE_TYPES_NUMBER, PRIMITIVE_TYPES_STRING_ENCODED_NUMBER } from '#layers/data-model/constants/schema/primitive-type';

export function getTypename<T extends PrimitiveType | PrimitiveTypeName, R = T extends PrimitiveType ? T['name'] : T>(type: T): R {
	return (typeof type === 'string' ? type : type.name) as R;
}

export function getTypeParams<T extends PrimitiveType>(type: T): PrimitiveTypeParams<T> {
	return ('params' in type ? type.params : undefined) as PrimitiveTypeParams<T>;
}

/**
 * The declared precision and scale, or `undefined` for each when the decimal is unconstrained.
 * Inputs read an absent bound as "no limit".
 */
export function getDecimalConstraints(type: PrimitiveType | undefined): { precision: number | undefined; scale: number | undefined } {
	const params = type && isPrimitiveTypeDecimal(type) ? getTypeParams(type) : undefined;

	if (params?.kind !== DecimalPrecisionKind.Constrained) {
		return { precision: undefined, scale: undefined };
	}

	return { precision: params.precision, scale: params.scale };
}

export function isSameType(type1: PrimitiveType | PrimitiveTypeName, type2: PrimitiveType | PrimitiveTypeName): boolean {
	return getTypename(type1) === getTypename(type2) && isDeepEqual(type1, type2);
}

export function isPrimitiveTypeNumber(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeNumber>;
export function isPrimitiveTypeNumber(type: PrimitiveType): type is PrimitiveTypeNumber;
export function isPrimitiveTypeNumber(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeNumber> | PrimitiveTypeNumber;

export function isPrimitiveTypeNumber(type: PrimitiveType | PrimitiveTypeName): boolean {
	return PRIMITIVE_TYPES_NUMBER.includes(getTypename(type) as typeof PRIMITIVE_TYPES_NUMBER[number]);
}

export function isPrimitiveTypeDateTime(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeDateTimeWithTimezone | PrimitiveTypeDateTime>;
export function isPrimitiveTypeDateTime(type: PrimitiveType): type is PrimitiveTypeDateTimeWithTimezone | PrimitiveTypeDateTime;
export function isPrimitiveTypeDateTime(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeDateTimeWithTimezone | PrimitiveTypeDateTime> | PrimitiveTypeDateTimeWithTimezone | PrimitiveTypeDateTime;

export function isPrimitiveTypeDateTime(type: PrimitiveType | PrimitiveTypeName): boolean {
	const typeName = getTypename(type);
	return typeName === 'dateTime' || typeName === 'dateTimeWithTimezone';
}

export function isPrimitiveTypeInteger(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeInteger>;
export function isPrimitiveTypeInteger(type: PrimitiveType): type is PrimitiveTypeInteger;
export function isPrimitiveTypeInteger(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeInteger> | PrimitiveTypeInteger;

export function isPrimitiveTypeInteger(type: PrimitiveType | PrimitiveTypeName): boolean {
	return PRIMITIVE_TYPES_INTEGER.includes(getTypename(type) as typeof PRIMITIVE_TYPES_INTEGER[number]);
}

export function isPrimitiveTypeInteger64(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeInteger64>;
export function isPrimitiveTypeInteger64(type: PrimitiveType): type is PrimitiveTypeInteger64;
export function isPrimitiveTypeInteger64(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeInteger64> | PrimitiveTypeInteger64;

export function isPrimitiveTypeInteger64(type: PrimitiveType | PrimitiveTypeName): boolean {
	return PRIMITIVE_TYPES_INTEGER_64.includes(getTypename(type) as typeof PRIMITIVE_TYPES_INTEGER_64[number]);
}

/** Numbers held as decimal strings because a double cannot represent them losslessly. */
export function isPrimitiveTypeStringEncodedNumber(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeStringEncodedNumber>;
export function isPrimitiveTypeStringEncodedNumber(type: PrimitiveType): type is PrimitiveTypeStringEncodedNumber;
export function isPrimitiveTypeStringEncodedNumber(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeStringEncodedNumber> | PrimitiveTypeStringEncodedNumber;

export function isPrimitiveTypeStringEncodedNumber(type: PrimitiveType | PrimitiveTypeName): boolean {
	return PRIMITIVE_TYPES_STRING_ENCODED_NUMBER.includes(getTypename(type) as typeof PRIMITIVE_TYPES_STRING_ENCODED_NUMBER[number]);
}

export function isPrimitiveTypeFloat(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeFloat>;
export function isPrimitiveTypeFloat(type: PrimitiveType): type is PrimitiveTypeFloat;
export function isPrimitiveTypeFloat(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeFloat> | PrimitiveTypeFloat;

export function isPrimitiveTypeFloat(type: PrimitiveType | PrimitiveTypeName): boolean {
	return PRIMITIVE_TYPES_FLOAT.includes(getTypename(type) as typeof PRIMITIVE_TYPES_FLOAT[number]);
}

export function isPrimitiveTypeUuid(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeUuid>;
export function isPrimitiveTypeUuid(type: PrimitiveType): type is PrimitiveTypeUuid;
export function isPrimitiveTypeUuid(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeUuid> | PrimitiveTypeUuid;

export function isPrimitiveTypeUuid(type: PrimitiveType | PrimitiveTypeName): boolean {
	return getTypename(type) === PRIMITIVE_TYPE_UUID;
}

export function isPrimitiveTypeBoolean(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeBoolean>;
export function isPrimitiveTypeBoolean(type: PrimitiveType): type is PrimitiveTypeBoolean;
export function isPrimitiveTypeBoolean(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeBoolean> | PrimitiveTypeBoolean;

export function isPrimitiveTypeBoolean(type: PrimitiveType | PrimitiveTypeName): boolean {
	return getTypename(type) === PRIMITIVE_TYPE_BOOLEAN;
}

export function isPrimitiveTypeString(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeString>;
export function isPrimitiveTypeString(type: PrimitiveType): type is PrimitiveTypeString;
export function isPrimitiveTypeString(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeString> | PrimitiveTypeString;

export function isPrimitiveTypeString(type: PrimitiveType | PrimitiveTypeName): boolean {
	return getTypename(type) === PRIMITIVE_TYPE_STRING;
}

export function isPrimitiveTypeJson(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeJson>;
export function isPrimitiveTypeJson(type: PrimitiveType): type is PrimitiveTypeJson;
export function isPrimitiveTypeJson(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeJson> | PrimitiveTypeJson;

export function isPrimitiveTypeJson(type: PrimitiveType | PrimitiveTypeName): boolean {
	return getTypename(type) === PRIMITIVE_TYPE_JSON;
}

export function isPrimitiveTypeDecimal(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeDecimal>;
export function isPrimitiveTypeDecimal(type: PrimitiveType): type is PrimitiveTypeDecimal;
export function isPrimitiveTypeDecimal(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeDecimal> | PrimitiveTypeDecimal;

export function isPrimitiveTypeDecimal(type: PrimitiveType | PrimitiveTypeName): boolean {
	return getTypename(type) === PRIMITIVE_TYPE_DECIMAL;
}

export function isPrimitiveTypeAnyDateTime(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeAnyDateTime>;
export function isPrimitiveTypeAnyDateTime(type: PrimitiveType): type is PrimitiveTypeAnyDateTime;
export function isPrimitiveTypeAnyDateTime(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeAnyDateTime> | PrimitiveTypeAnyDateTime;

export function isPrimitiveTypeAnyDateTime(type: PrimitiveType | PrimitiveTypeName): boolean {
	return PRIMITIVE_TYPES_DATETIME.includes(getTypename(type) as typeof PRIMITIVE_TYPES_DATETIME[number]);
}

export function isPrimitiveTypeUnsupported(type: PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeUnsupported>;
export function isPrimitiveTypeUnsupported(type: PrimitiveType): type is PrimitiveTypeUnsupported;
export function isPrimitiveTypeUnsupported(type: PrimitiveType | PrimitiveTypeName): type is InferPrimitiveTypeName<PrimitiveTypeUnsupported> | PrimitiveTypeUnsupported;

export function isPrimitiveTypeUnsupported(type: PrimitiveType | PrimitiveTypeName): boolean {
	return getTypename(type) === PRIMITIVE_TYPE_UNSUPPORTED;
}
